'use client';
import React from 'react';
import { useTheme } from '@/lib/ThemeContext';

interface MobileBottomNavProps {
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
  onSearchFocus: () => void;
}

export default function MobileBottomNav({ activeCategory, onCategoryChange, onSearchFocus }: MobileBottomNavProps) {
  const isHome = activeCategory === 'All';
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const inactiveColor = isDark ? '#475569' : '#94a3b8';

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
      style={{
        background: isDark ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.88)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderTop: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.65)',
        boxShadow: isDark ? '0 -4px 24px rgba(0,0,0,0.35)' : '0 -4px 24px rgba(15,23,42,0.08)',
      }}
    >
      <div className="flex items-center justify-around h-16 px-2">
        {/* Home */}
        <button
          onClick={() => onCategoryChange('All')}
          className="flex flex-col items-center gap-1 px-3 py-1 rounded-xl transition-all duration-200"
          aria-label="Home"
        >
          <svg
            className="w-5 h-5 transition-colors"
            fill={isHome ? '#3b82f6' : 'none'}
            viewBox="0 0 24 24"
            stroke={isHome ? '#3b82f6' : inactiveColor}
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
          </svg>
          <span
            className="text-[10px] font-semibold transition-colors"
            style={{ color: isHome ? '#3b82f6' : inactiveColor }}
          >
            Home
          </span>
        </button>

        {/* Search */}
        <button
          onClick={onSearchFocus}
          className="flex flex-col items-center gap-1 px-3 py-1 rounded-xl transition-all duration-200"
          aria-label="Search"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke={inactiveColor} strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <span className="text-[10px] font-semibold" style={{ color: inactiveColor }}>Search</span>
        </button>

        {/* PDF */}
        <button
          onClick={() => onCategoryChange('PDF Editing')}
          className="flex flex-col items-center gap-1 px-3 py-1 rounded-xl transition-all duration-200"
          aria-label="PDF Tools"
        >
          <svg
            className="w-5 h-5 transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            stroke={activeCategory === 'PDF Editing' ? '#ef4444' : inactiveColor}
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <span
            className="text-[10px] font-semibold transition-colors"
            style={{ color: activeCategory === 'PDF Editing' ? '#ef4444' : inactiveColor }}
          >
            PDF
          </span>
        </button>

        {/* Image */}
        <button
          onClick={() => onCategoryChange('Image Edit')}
          className="flex flex-col items-center gap-1 px-3 py-1 rounded-xl transition-all duration-200"
          aria-label="Image Tools"
        >
          <svg
            className="w-5 h-5 transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            stroke={activeCategory === 'Image Edit' ? '#3b82f6' : inactiveColor}
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
          <span
            className="text-[10px] font-semibold transition-colors"
            style={{ color: activeCategory === 'Image Edit' ? '#3b82f6' : inactiveColor }}
          >
            Image
          </span>
        </button>

        {/* Audio */}
        <button
          onClick={() => onCategoryChange('Audio Tools')}
          className="flex flex-col items-center gap-1 px-3 py-1 rounded-xl transition-all duration-200"
          aria-label="Audio Tools"
        >
          <svg
            className="w-5 h-5 transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            stroke={activeCategory === 'Audio Tools' ? '#f59e0b' : inactiveColor}
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
          </svg>
          <span
            className="text-[10px] font-semibold transition-colors"
            style={{ color: activeCategory === 'Audio Tools' ? '#f59e0b' : inactiveColor }}
          >
            Audio
          </span>
        </button>
      </div>
    </nav>
  );
}
